import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, Subscription, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { environment } from "src/environments/environment";
import { IAccount } from "../_models/account/IAccount";
import { CookieService } from "ngx-cookie-service";
import { IRole } from "../_models/account/IRole";
import jwt_decode from "jwt-decode";
import { RoleMappings } from "../_enums/RoleMappings";

@Injectable({
  providedIn: "root",
})
export class AuthenticationService implements OnDestroy {
  account = new BehaviorSubject<IAccount>(null);
  subscriptions = new Subscription();
  timer: any;

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private router: Router
  ) {}

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  doLogin(user: string, pass: string) {
    localStorage.setItem("isTokenRefreshing", "0");

    if (localStorage.getItem("newLogin") === "1") {
      localStorage.setItem("newLogin", "0");
    }
    else {
      localStorage.setItem("newLogin", "1");
    }

    return this.http
      .post(environment.API_URL + "accounts/authenticate", {
        username: user,
        password: pass,
      })
      .pipe(
        map((res: IAccount) => {
          if (res) {
            this.handleAuthentication(res);

            return true;
          }
        }),
        catchError((error) => {
          let errorMessage = "";
          if (error.error.errors) {
            const errors = error.error.errors;

            if (typeof errors === "object") {
              const errorsList = [];
              for (const prop in errors) {
                if (errors.hasOwnProperty(prop)) {
                  errorsList.push(errors[prop]);
                }
              }

              errorMessage = errorsList.join("<br />");
            } else {
              errorMessage = errors;
            }
          } else {
            errorMessage = error.error.message;
          }

          // verificam daca trebuie sa afisam butonul pentru trimiterea din nou a emailului de validare a contului
          let sendEmail = false;
          if (error.error.data && error.error.data?.sendEmail) {
            sendEmail = error.error.data.sendEmail;
          }

          const data = {
            error: errorMessage,
            data: {
              sendEmail: sendEmail,
              accountID:
                error.error.data && error.error.data?.accountID
                  ? error.error.data.accountID
                  : 0,
            },
          };

          return throwError(data);
        })
      );
  }

  doLogout() {
    return this.http.post(environment.API_URL + "accounts/logout", {}).pipe(
      map((res) => {
        if (res) {
          this.account.next(null);
          this.clientLogout();
          return true;
        }
      })
    );
  }

  doLogoutOnly() {
    return this.http.post(environment.API_URL + "accounts/logout", {}).pipe(
      map((res) => {
        if (res) {
          this.account.next(null);
          this.deleteTokenCookie();
          localStorage.setItem("isTokenRefreshing", "0");
          localStorage.setItem("session", "0");
          localStorage.setItem("newLogin", "");
        }
      })
    );
  }

  clientLogout(withRedirect = true) {
    this.deleteTokenCookie();
    localStorage.setItem("isTokenRefreshing", "0");
    localStorage.setItem("session", "0");
    localStorage.setItem("newLogin", "");
    if (withRedirect) {
      window.location.href = "/";
    } else {
      this.router.navigate(["/"]);
    }
    this.account.next(null);
  }

  clientLandingLogout() {
    this.deleteTokenCookie();
    localStorage.setItem("isTokenRefreshing", "0");
    localStorage.setItem("session", "0");
    localStorage.setItem("newLogin", "");
  }

  getRefreshToken(refToken: string) {
    const account = this.account.getValue();

    return this.http
      .post(environment.API_URL + "accounts/refresh-token", {
        refreshToken: refToken,
      })
      .pipe(
        map((res: IAccount) => {
          account.expiresIn = res.expiresIn;
          account.token = res.token;
          account.refreshToken = res.refreshToken;

          this.account.next(account);
          this.cookieService.delete("accountData");
          this.setTokenCookie(account.token);

          return res.token;
        }),
        catchError(() => {
          // emitem o notificare cu null, pentru situatia cand refreshTokenul a expirat
          return throwError(null);
        })
      );
  }

  autoLogin() {
    const accountID = this.decodeToken();
    if (accountID) {
      this.accountDetails(accountID).subscribe(
        (res: IAccount) => {
          this.account.next(res);
        }
      );
    }
  }

  getToken() {
    if (this.cookieService.get("token") !== "") {
      return this.cookieService.get("token");
    }

    return null;
  }

  isAuthenticated() {
    const account = this.account.value;
    if (account) {
      return account;
    }

    return false;
  }

  isOnlyAuthenticated() {
    return this.account.value !== null;
  }

  getAccount() {
    return this.account.value;
  }

  getAccountID() {
    return this.account.value.id;
  }

  getAccountRoles(): IRole[] {
    return this.account.value.roles;
  }

  hasRoleByMapping(roleMapping: number) {
    return (
      this.account.value.roles.find((x) => x.mapping === roleMapping) !==
      undefined
    );
  }

  getName() {
    if (this.account.value) {
      return this.account.value.person.lastName + " " + this.account.value.person.firstName;
    }

    return null;
  }

  getRoles() {
    if (this.account.value) {
        return this.account.value.roles;
    }

    return null;
  }

  isAdmin() {
    return this.hasRoleByMapping(RoleMappings.Admin);
  }

  isContributor() {
    return this.hasRoleByMapping(RoleMappings.Contributor);
  }

  isDoctor() {
    return this.hasRoleByMapping(RoleMappings.Doctor);
  }

  isPatient() {
    return this.hasRoleByMapping(RoleMappings.Patient);
  }

  setTokenCookie(token: string) {
    this.cookieService.set("token", token, {
      path: "/",
    });
  }

  deleteTokenCookie() {
    this.cookieService.delete("token", "/");
  }

  private handleAuthentication(account: IAccount) {
    this.account.next(account);
    this.setTokenCookie(account.token);
  }

  private accountDetails(id: number) {
    return this.http.get(environment.API_URL + "accounts/" + id).pipe(
      map((data: IAccount) => {
        return data;
      })
    );
  }

  private decodeToken(): number {
    if (this.cookieService.check("token")) {
      const token = this.cookieService.get("token");
      if (token) {
        const decodedToken = jwt_decode(token);
        if (decodedToken && decodedToken instanceof Object) {
          // @ts-ignore
          const accountID = Number(decodedToken.id);
          if (!isNaN(accountID)) {
            return accountID;
          }
        }
      }
    }

    return null;
  }
}
